import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import NpsModal from "../../../../components/NpsModal";
import constants from "../../../../constants";
import { NpsStatus, npsService } from "../../../../services/nps";
import { releasesStatus } from "../../../../services/releases";
import { uuidv4 } from "../../../../utils/uuid";

/** How long after the Keepos list shows the survey comes up (ms): the page first, then the question */
export const NPS_SHOW_DELAY = 3000;

/**
 * The NPS survey on the Keepos list, at most once per browser session, when the API says the user is due
 * (see NpsSurvey::isEligible() in the API), or on every visit in test mode (VITE_APP_NPS_TEST=true). Every
 * step is saved: shown, the score, the comment, closed.
 */
const NpsPrompt = () => {
  const { i18n } = useTranslation();
  const [surveyId, setSurveyId] = useState<string>();
  // The score goes again with the comment: if its own step was lost, the comment still saves the answer
  const score = useRef<number>();
  const context = useRef({
    locale: i18n.language,
    device: (window.innerWidth < 768 ? "mobile" : "desktop") as "mobile" | "desktop",
  });

  useEffect(() => {
    // Test mode (VITE_APP_NPS_TEST): every visit, whatever the rules
    const isTest = constants.npsTest;
    if (!isTest && npsService.wasChecked()) {
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    (isTest ? Promise.resolve(true) : npsService.isEligible())
      .then(eligible => {
        if (cancelled) {
          return;
        }
        if (!eligible) {
          npsService.markChecked();
          return;
        }
        // Leaving the page before it shows: asked again next time the list is opened
        timer = setTimeout(async () => {
          // A launch being announced (ReleaseAnnouncement) goes first: the survey waits for the next visit. The news
          // may still be on their way: deciding before they arrive could show both
          await releasesStatus.settled();
          if (cancelled || releasesStatus.get().announce) {
            return;
          }
          npsService.markChecked();
          const id = uuidv4();
          score.current = undefined;
          setSurveyId(id);
          npsService.save({ surveyId: id, status: "shown", context: context.current });
        }, NPS_SHOW_DELAY);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, []);

  if (!surveyId) {
    return null;
  }

  const save = (status: NpsStatus, answer: { score?: number; comment?: string } = {}) =>
    npsService.save({ surveyId, status, ...answer, context: context.current });

  return (
    <NpsModal
      onScore={value => {
        score.current = value;
        save("answered", { score: value });
      }}
      onComment={comment => save("answered", { score: score.current, comment })}
      onDismiss={() => save("dismissed")}
      onClose={() => setSurveyId(undefined)}
    />
  );
};

export default NpsPrompt;
